import { useState } from 'react';
import { useLoaderData, useNavigate } from 'react-router';
import type { LoaderFunctionArgs } from 'react-router';
import { 
  Bot, Zap, Play, Pause, Settings, TrendingUp, Clock, 
  CheckCircle, AlertTriangle, Plus, BarChart3, Eye, Edit, 
  Trash2, Copy, MoreHorizontal, Calendar
} from 'lucide-react';
import { requireUser } from '../../../lib/session.server';
import { autoApplyApi } from '../../../lib/api';
import { Card, Button, Badge } from '../../../components/ui';
import { StatusChip, useToastActions } from '../../../components/ui';
import type { 
  AutoApplyDashboardStats, 
  AutoApplyRulesResponse, 
  AutoApplyRuleStatus,
  User 
} from '../../../types';

interface AutoApplyData {
  user: User;
  dashboardStats: AutoApplyDashboardStats;
  rulesResponse: AutoApplyRulesResponse;
}

export async function loader({ request }: LoaderFunctionArgs) {
  const user = await requireUser(request);
  const url = new URL(request.url);
  
  const page = parseInt(url.searchParams.get('page') || '1', 10);
  const status = url.searchParams.get('status') as AutoApplyRuleStatus | undefined;
  const search = url.searchParams.get('search') || '';
  
  try {
    const [dashboardStats, rulesResponse] = await Promise.all([
      autoApplyApi.getDashboardStats(),
      autoApplyApi.getRules({
        page,
        pageSize: 20,
        ...(status && { status }),
        ...(search && { search }),
      }),
    ]);

    return {
      user,
      dashboardStats,
      rulesResponse,
    };
  } catch (error) {
    const emptyDashboard: AutoApplyDashboardStats = {
      total_rules: 0,
      active_rules: 0,
      total_applications_today: 0,
      total_applications_this_week: 0,
      total_applications_all_time: 0,
      success_rate: 0,
      active_executions: 0,
    };
    
    const emptyRules: AutoApplyRulesResponse = {
      rules: [],
      total_count: 0,
      page: 1,
      page_size: 20,
      has_more: false,
    };
    
    return {
      user,
      dashboardStats: emptyDashboard,
      rulesResponse: emptyRules,
      error: 'Failed to load auto-apply data',
    };
  }
}

export function AutoApplyPage() {
  const { dashboardStats, rulesResponse } = useLoaderData() as AutoApplyData;
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const { success, error } = useToastActions();

  const { rules, total_count, page, has_more } = rulesResponse;

  const handleToggleRule = async (ruleId: string) => {
    try {
      await autoApplyApi.toggleRuleStatus(ruleId);
      success('Rule Updated', 'Rule status has been changed.');
      window.location.reload();
    } catch (err) {
      error('Update Failed', 'Unable to update rule status.');
    }
  };

  const handleTestRule = async (ruleId: string) => {
    try {
      await autoApplyApi.testRule(ruleId);
      success('Test Started', 'Dry run started. Check execution logs for results.');
    } catch (err) {
      error('Test Failed', 'Unable to start test run.');
    }
  };

  const handleExecuteRule = async (ruleId: string) => {
    if (confirm('Are you sure you want to execute this rule immediately?')) {
      try {
        await autoApplyApi.executeRule(ruleId);
        success('Execution Started', 'Rule execution has been started.');
      } catch (err) {
        error('Execution Failed', 'Unable to start rule execution.');
      }
    }
  };

  const handleCopyRule = async (ruleId: string) => {
    try {
      await autoApplyApi.copyRule(ruleId);
      success('Rule Copied', 'Rule has been duplicated successfully.');
      // Refresh the page to show the new rule
      window.location.reload();
    } catch (err) {
      error('Copy Failed', 'Unable to copy the rule.');
    }
  };

  const handleDeleteRule = async (ruleId: string) => {
    if (confirm('Are you sure you want to delete this rule? This action cannot be undone.')) {
      try {
        await autoApplyApi.deleteRule(ruleId);
        success('Rule Deleted', 'Auto-apply rule has been removed.');
        window.location.reload();
      } catch (err) {
        error('Delete Failed', 'Unable to delete rule.');
      }
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <Bot className="h-6 w-6 text-primary" />
          <h1 className="text-3xl font-bold text-foreground">Auto-Apply</h1>
          <Badge variant="secondary" className="gap-1">
            <Zap className="h-3 w-3" />
            BETA
          </Badge>
        </div>
        <p className="text-muted-foreground">
          Automate your job applications with AI-powered rules and smart targeting.
        </p>
      </div>

      {/* Dashboard Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Total Rules</p>
              <p className="text-2xl font-bold text-foreground">{dashboardStats.total_rules}</p>
            </div>
            <div className="p-2 bg-primary/10 rounded-xl">
              <Settings className="h-5 w-5 text-primary" />
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Active Rules</p>
              <p className="text-2xl font-bold text-foreground">{dashboardStats.active_rules}</p>
            </div>
            <div className="p-2 bg-green-500/10 rounded-xl">
              <Play className="h-5 w-5 text-green-500" />
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Applications Today</p>
              <p className="text-2xl font-bold text-foreground">{dashboardStats.total_applications_today}</p>
            </div>
            <div className="p-2 bg-blue-500/10 rounded-xl">
              <TrendingUp className="h-5 w-5 text-blue-500" />
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Success Rate</p>
              <p className="text-2xl font-bold text-foreground">{(dashboardStats.success_rate * 100).toFixed(1)}%</p>
            </div>
            <div className="p-2 bg-amber-500/10 rounded-xl">
              <CheckCircle className="h-5 w-5 text-amber-500" />
            </div>
          </div>
        </Card>
      </div>

      {/* Action Bar */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between">
        <div className="flex-1 max-w-md">
          <div className="relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search auto-apply rules..."
              className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>
        </div>

        <div className="flex gap-2">
          <Button 
            onClick={() => navigate('/auto-apply/analytics')} 
            variant="secondary" 
            size="sm" 
            className="gap-1"
          >
            <BarChart3 className="h-4 w-4" />
            Analytics
          </Button>
          <Button onClick={() => navigate('/auto-apply/create')} className="gap-2">
            <Plus className="h-4 w-4" />
            Create Rule
          </Button>
        </div>
      </div>

      {/* Next Execution */}
      {dashboardStats.next_execution && (
        <Card>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-xl">
                <Clock className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h3 className="font-medium text-foreground">Next Scheduled Execution</h3>
                <p className="text-sm text-muted-foreground">
                  <strong>{dashboardStats.next_execution.rule_name}</strong> will run {formatDate(dashboardStats.next_execution.scheduled_at)}
                </p>
              </div>
            </div>
            <Button variant="secondary" size="sm" className="gap-1">
              <Calendar className="h-4 w-4" />
              View Schedule
            </Button>
          </div>
        </Card>
      )}

      {/* Rules List */}
      {rules.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <Bot className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-foreground mb-2">No Auto-Apply Rules</h3>
            <p className="text-muted-foreground mb-4 max-w-md mx-auto">
              Create your first auto-apply rule to start automating your job applications. 
              Set up search criteria, schedules, and let AI handle the applications for you.
            </p>
            <Button onClick={() => navigate('/auto-apply/create')} className="gap-2">
              <Plus className="h-4 w-4" />
              Create Your First Rule
            </Button>
          </div>
        </Card>
      ) : (
        <>
          <div className="space-y-4">
            {rules.map((rule) => (
              <Card 
                key={rule.id} 
                className={`transition-all hover:shadow-lg ${
                  rule.status === 'active' ? 'ring-1 ring-green-500/20 border-green-500/30' : ''
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-xl ${
                        rule.status === 'active' ? 'bg-green-500/10' :
                        rule.status === 'paused' ? 'bg-amber-500/10' : 'bg-red-500/10'
                      }`}>
                        <Bot className={`h-5 w-5 ${
                          rule.status === 'active' ? 'text-green-500' :
                          rule.status === 'paused' ? 'text-amber-500' : 'text-red-500'
                        }`} />
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-foreground text-lg">{rule.name}</h3>
                          <StatusChip
                            status={rule.status}
                            color={rule.status === 'active' ? 'green' : 
                                   rule.status === 'paused' ? 'amber' : 'red'}
                          >
                            {rule.status.charAt(0).toUpperCase() + rule.status.slice(1)}
                          </StatusChip>
                        </div>
                        
                        {rule.description && (
                          <p className="text-muted-foreground text-sm mb-2">{rule.description}</p>
                        )}
                        
                        {/* Rule Stats */}
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span>
                            <strong className="text-foreground">{rule.stats.total_applications}</strong> applications
                          </span>
                          <span>
                            <strong className="text-foreground">{(rule.stats.success_rate * 100).toFixed(1)}%</strong> success rate
                          </span>
                          <span>Last run: {formatDate(rule.last_run_at)}</span>
                        </div>
                        
                        {/* Search Criteria Preview */}
                        <div className="flex flex-wrap gap-2 mt-2">
                          {rule.search_criteria.keywords.slice(0, 3).map((keyword, index) => (
                            <Badge key={index} variant="secondary" className="text-xs">
                              {keyword}
                            </Badge>
                          ))}
                          {rule.search_criteria.keywords.length > 3 && (
                            <Badge variant="secondary" className="text-xs">
                              +{rule.search_criteria.keywords.length - 3} more
                            </Badge>
                          )}
                        </div>
                        
                        {/* Active Execution Warning */}
                        {dashboardStats.active_executions > 0 && (
                          <div className="flex items-center gap-2 mt-2 text-amber-600">
                            <AlertTriangle className="h-4 w-4" />
                            <span className="text-sm">Execution in progress...</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-2 ml-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate(`/auto-apply/rules/${rule.id}`)}
                      className="gap-1 opacity-60 hover:opacity-100"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleTestRule(rule.id)}
                      className="gap-1 opacity-60 hover:opacity-100"
                      title="Test run (dry run)"
                    >
                      <Settings className="h-4 w-4" />
                    </Button>
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleExecuteRule(rule.id)}
                      className="gap-1 opacity-60 hover:opacity-100 text-blue-600 hover:text-blue-700"
                      title="Execute rule now"
                      disabled={rule.status !== 'active'}
                    >
                      <Zap className="h-4 w-4" />
                    </Button>
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleToggleRule(rule.id)}
                      className={`gap-1 opacity-60 hover:opacity-100 ${
                        rule.status === 'active' 
                          ? 'text-amber-600 hover:text-amber-700' 
                          : 'text-green-600 hover:text-green-700'
                      }`}
                    >
                      {rule.status === 'active' ? (
                        <Pause className="h-4 w-4" />
                      ) : (
                        <Play className="h-4 w-4" />
                      )}
                    </Button>
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate(`/auto-apply/rules/${rule.id}/edit`)}
                      className="gap-1 opacity-60 hover:opacity-100"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleCopyRule(rule.id)}
                      className="gap-1 opacity-60 hover:opacity-100 text-blue-600 hover:text-blue-700"
                      title="Copy rule"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteRule(rule.id)}
                      className="gap-1 opacity-60 hover:opacity-100 hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      className="gap-1 opacity-60 hover:opacity-100"
                      title="More options"
                    >
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {/* Pagination */}
          {(has_more || total_count > 0) && (
            <div className="flex flex-col items-center gap-4">
              {total_count > 0 && (
                <div className="text-sm text-muted-foreground">
                  Showing {rules.length} of {total_count} rules
                  {page > 1 && ` • Page ${page}`}
                </div>
              )}
              {has_more && (
                <Button variant="secondary">
                  Load More Rules
                </Button>
              )}
            </div>
          )}
        </>
      )}

      {/* Beta Notice */}
      <Card>
        <div className="bg-blue-500/10 border border-blue-500/20 rounded-2xl p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-foreground mb-1">Auto-Apply Beta</h4>
              <p className="text-sm text-muted-foreground">
                Auto-Apply is currently in beta. Please review all applications before they are submitted. 
                We recommend starting with test runs and monitoring execution logs closely.
              </p>
              <div className="flex gap-2 mt-2">
                <Button size="sm" variant="secondary" onClick={() => navigate('/auto-apply/help')}>
                  Learn More
                </Button>
                <Button size="sm" variant="secondary" onClick={() => navigate('/settings/auto-apply')}>
                  Configure Settings
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}